import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  CalendarClock,
  Database,
  GitMerge,
  LayoutDashboard,
  Library,
  Lock,
  MessageSquare,
  Radar,
  ScanSearch,
  ShoppingCart,
  Stethoscope,
  Search,
  type LucideIcon,
} from 'lucide-react';

import { NAV_GROUPS, NAV_ITEMS } from '@/app/navigation';

const ICONS: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  connectors: Database,
  catalog: Library,
  quality: Activity,
  mdm: GitMerge,
  domains: ScanSearch,
  chat: MessageSquare,
  reporting: BarChart3,
  scheduler: CalendarClock,
  observability: Radar,
  privacy: Lock,
  marketplace: ShoppingCart,
  diagnostics: Stethoscope,
};

export function Sidebar(): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <aside className="flex w-64 shrink-0 flex-col bg-sidebar-gradient text-slate-300">
      {/* Brand */}
      <div className="flex h-16 items-center gap-2.5 px-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-400 to-brand-700 text-base font-bold text-white shadow-glow">
          n
        </div>
        <span className="font-display text-[17px] font-semibold tracking-tight text-white">
          nlytics
        </span>
      </div>

      <div className="px-5 pb-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search menu..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-md bg-white/10 py-2 pl-9 pr-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
      </div>

      {/* Grouped nav */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {NAV_GROUPS.map((group, idx) => {
          const items = NAV_ITEMS.filter(
            (n) =>
              n.group === group.id &&
              n.label.toLowerCase().includes(searchQuery.toLowerCase())
          );
          if (items.length === 0) return null;
          return (
            <div key={group.id} className={idx === 0 ? 'mt-2' : 'mt-5'}>
              <div className="flex items-center gap-2 px-3 pb-2">
                <span className="font-mono text-[10px] font-semibold tracking-[0.22em] text-slate-500">
                  {group.label.toUpperCase()}
                </span>
                <span className="h-px flex-1 bg-white/5" />
              </div>
              <div className="space-y-0.5">
                {items.map((item) => {
                  const Icon = ICONS[item.key] ?? LayoutDashboard;
                  return (
                    <NavLink
                      key={item.key}
                      to={item.path}
                      end={item.path === '/'}
                      className={({ isActive }) =>
                        `group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition ${
                          isActive
                            ? 'bg-white/[0.06] font-medium text-white shadow-[inset_2px_0_0_0_theme(colors.brand.400)]'
                            : 'text-slate-400 hover:bg-white/[0.04] hover:text-white'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <Icon
                            className={`h-[17px] w-[17px] shrink-0 transition ${
                              isActive
                                ? 'text-brand-300'
                                : 'text-slate-500 group-hover:text-slate-300'
                            }`}
                          />
                          <span className="truncate">{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="border-t border-white/5 px-5 py-3 font-mono text-[10px] tracking-[0.18em] text-slate-500">
        DATA MANAGEMENT PLATFORM
      </div>
    </aside>
  );
}
